import { isNil, isEmpty, forEach, assign, clone } from 'lodash';
import RSA from 'node-rsa';
import LoginAPI from '@/api/login';


/**
 * 데이터 객체의 각 필드 값을 RSA 방식으로 암호화하여 반환한다.
 * @author Ju
 * @param  {Object} data
 * @param  {String} publicKey
 * @return {Object}
 */
function encrypt(data, publicKey) {
  const rsaKey = new RSA({ b: 1024 });
  rsaKey.setOptions({
    encryptSchemes: {
      scheme: 'pkcs1-nopadding',
    },
  });
  rsaKey.importKey({
    n: Buffer.from(publicKey.pkm, 'hex'),
    e: parseInt(publicKey.pke, 16),
  }, 'components-public');
  // 들어온 데이터를 암호화 한다.
  const result = {};
  // data 데이터 구조 field: 데이터 필드 명 , value: 데이터 값
  forEach(data, (value, key) => {
    if (!isNil(value)) {
      result[key] = rsaKey.encrypt(value, 'hex', 'utf8');
    }
  });
  return result;
}

/**
 * 암호화가 필요한 필드들의 값을 가진 객체를 반환한다.
 * @author Ju
 * @param  {Object} data                 암호화 필요 데이터 추출용 데이터 객체
 * @param  {Array} requireEncryptFields 암호화가 필요한 필드 정보 배열
 * @return {Object}                      암호화가 필요한 필드들의 값을 가진 객체
 */
export function createEncryptTargetData(data, requireEncryptFields) {
  if (isEmpty(data) || isEmpty(requireEncryptFields)) {
    return null;
  }
  const encryptTargetData = {};
  forEach(requireEncryptFields, (field) => {
    if (!isNil(data[field]) && data[field] !== '') {
      encryptTargetData[field] = data[field];
    }
  });
  return isEmpty(encryptTargetData) ? null : encryptTargetData;
}

/**
 * 데이터 객체의 각 필드 값을 RSA 방식으로 암호화하여 반환한다.
 * @param  {Object} data 암호화할 데이터 객체 (key, value 쌍으로 value 값을 RSA 방식으로 암호화한다.)
 * @return {Object}      암호화된 데이터 객체
 *
 * ex) params: { managerId: 'administrator', passwrod: '1234'} → return { managerId: '암호화된정보', password: '암호화된 정보'}
 */
export function getRsaEncrypt(data) {
  return new Promise((resolve, reject) => {
    // 키 발급
    LoginAPI.getLoginKey()
      .then((response) => {
        if (!isNil(response.data)) {
          // RSA 암호화 처리
          const publicKey = response.data;
          const result = encrypt(data, publicKey);
          resolve(result);
        } else {
          reject(false);
        }
      })
      .catch(() => {
        reject(false);
      });
  });
}

/**
 * 일괄적으로 각 필드 값을 RSA 방식으로 암호화한다.
 * @author Ju
 * @param  {Array} datas                데이터 배열
 * @param  {Array} requireEncryptFields 암호화 필드 배열
 * @return {Array}
 */
export function getRsaEncryptBulk(datas, requireEncryptFields) {
  return new Promise((resolve, reject) => {
    // 키 발급
    LoginAPI.getLoginKey()
      .then((response) => {
        if (!isNil(response.data)) {
          // RSA 암호화 처리
          const publicKey = response.data;
          const result = [];
          forEach(datas, (data) => {
            const encryptData = createEncryptTargetData(data, requireEncryptFields);
            result.push(encryptData === null ? data : assign(clone(data), encrypt(encryptData, publicKey)));
          });
          resolve(result);
        } else {
          reject(false);
        }
      })
      .catch(() => {
        reject(false);
      });
  });
}


export default {
  getRsaEncrypt,
};



// WEBPACK FOOTER //
// ./src/utils/rsa-encrypt.js